import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import { PROFILE_CONSENT_COOKIE_CONSENT_NAMES } from 'bundles/user-consent/constants';
import AddProfileConsentMutation from 'bundles/user-consent/queries/ProfileConsentsMutation.graphql';
import type {
  AddProfileConsentMutation as AddProfileConsentMutationType,
  AddProfileConsentMutationVariables,
} from 'bundles/user-consent/queries/__generated__/ProfileConsentsMutation';
import { setProfileConsentCookie } from 'bundles/user-consent/utils';

export const useAddProfileConsent = () => {
  const [addProfileConsentMutation] = useMutation<AddProfileConsentMutationType, AddProfileConsentMutationVariables>(
    AddProfileConsentMutation,
    {
      variables: {
        consentToTermsOfUse: true,
      },
      context: { clientName: 'gatewayGql' },
    }
  );

  const addProfileConsent = async () => {
    try {
      const { errors, data } = await addProfileConsentMutation();

      if (errors) {
        Sentry.captureException(errors);
      } else if (data?.ProfileConsents_AddProfileConsents.__typename === 'ProfileConsents_AddFailure') {
        Sentry.captureException(data.ProfileConsents_AddProfileConsents.errors);
      } else if (
        data?.ProfileConsents_AddProfileConsents.__typename === 'ProfileConsents_AddSuccess' &&
        data.ProfileConsents_AddProfileConsents.consents?.requiresTermsOfUseConsent !== false
      ) {
        Sentry.captureException('Profile Consent for Terms of Use has not been properly updated');
      } else {
        setProfileConsentCookie({ [PROFILE_CONSENT_COOKIE_CONSENT_NAMES.touRequired]: false });
        return data?.ProfileConsents_AddProfileConsents?.consents?.requiresTermsOfUseConsent;
      }
    } catch (error) {
      Sentry.captureException(error);
    }

    // Always fallback to assuming acceptance
    setProfileConsentCookie({ [PROFILE_CONSENT_COOKIE_CONSENT_NAMES.touRequired]: false });
    return null;
  };

  return addProfileConsent;
};

export default useAddProfileConsent;
